import {
  ArrowLeftRight,
  ChartNoAxesCombined,
  Globe,
  Landmark,
  Mail,
  Percent,
  Rss,
  ScrollText,
  Settings,
  ShoppingBag,
  Store,
  Truck,
  Undo2,
  Warehouse,
} from 'lucide-react';

import { defineTour, type TourResolver, type TourStep } from '@nucleus-commerce/admin-kit/tour';
import { getAdminV1StoresStoreIdPolicies } from '@nucleus-commerce/api-client/admin';

/** Optional first step of every settings screen that shows the read-only notice. */
const readOnlyStep: TourStep = {
  id: 'read-only',
  kind: 'explain',
  target: 'settings.read-only',
  optional: true,
  waitMs: 1000,
};

export const settingsHubTour = defineTour({
  id: 'settings.hub',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/org/stores/$storeId',
  scope: 'store',
  search: { tab: 'settings' },
  icon: Settings,
  steps: [
    {
      id: 'read-only',
      kind: 'explain',
      target: 'settings.read-only',
      optional: true,
      waitMs: 1000,
    },
    { id: 'tabs', kind: 'explain', target: 'settings.hub.tabs' },
    {
      id: 'search',
      kind: 'action',
      target: 'settings.hub.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'selling', kind: 'explain', target: 'settings.hub.group.selling', optional: true },
    { id: 'orders', kind: 'explain', target: 'settings.hub.group.orders', optional: true },
    {
      id: 'online-store',
      kind: 'explain',
      target: 'settings.hub.group.online-store',
      optional: true,
    },
    {
      id: 'launch-checklist',
      kind: 'explain',
      target: 'settings.hub.launch-checklist',
      requires: 'stores.read',
      optional: true,
    },
  ],
  related: ['settings.store-details', 'settings.payment-methods'],
});

export const storeDetailsTour = defineTour({
  id: 'settings.store-details',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/org/stores/$storeId',
  scope: 'store',
  search: { tab: 'details' },
  requires: 'stores.read',
  icon: Store,
  steps: [
    readOnlyStep,
    {
      id: 'profile',
      kind: 'explain',
      target: 'settings.store-details.profile',
      placement: 'right',
    },
    { id: 'order-numbers', kind: 'explain', target: 'settings.store-details.order-numbers' },
    { id: 'currencies', kind: 'explain', target: 'settings.store-details.currencies' },
    { id: 'checkout', kind: 'explain', target: 'settings.store-details.checkout' },
    { id: 'seo', kind: 'explain', target: 'settings.store-details.seo' },
    {
      id: 'save',
      kind: 'explain',
      target: 'settings.store-details.save',
      placement: 'top',
      requires: ['stores.write', 'settings.write'],
      optional: true,
    },
  ],
  related: ['settings.hub', 'settings.markets', 'settings.policies'],
});

export const marketsTour = defineTour({
  id: 'settings.markets',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/markets',
  scope: 'store',
  requires: 'settings.read',
  icon: Globe,
  steps: [
    readOnlyStep,
    { id: 'intro', kind: 'intro' },
    {
      id: 'markets',
      kind: 'explain',
      variants: [
        { target: 'settings.markets.card' },
        { target: 'settings.markets.empty', copy: 'empty' },
      ],
    },
    { id: 'exchange-rates', kind: 'explain', target: 'settings.markets.exchange-rates' },
    {
      id: 'add',
      kind: 'action',
      target: 'settings.markets.add',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['store-catalog.product', 'settings.tax'],
});

export const paymentMethodsTour = defineTour({
  id: 'settings.payment-methods',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/payment-methods',
  scope: 'store',
  requires: 'payments.manage',
  icon: Landmark,
  steps: [
    readOnlyStep,
    {
      id: 'stripe',
      kind: 'explain',
      target: 'settings.payment-methods.stripe',
      requires: 'settings.read',
    },
    {
      id: 'stripe-connect',
      kind: 'explain',
      target: 'settings.payment-methods.stripe-connect',
      requires: 'settings.write',
      optional: true,
      waitMs: 1500,
    },
    {
      id: 'apple-pay',
      kind: 'explain',
      target: 'settings.payment-methods.apple-pay',
      requires: 'settings.read',
      optional: true,
      waitMs: 1500,
    },
    {
      id: 'unpaid-hold',
      kind: 'explain',
      target: 'settings.payment-methods.unpaid-hold',
      requires: 'settings.read',
      optional: true,
    },
    {
      id: 'manual',
      kind: 'explain',
      variants: [
        { target: 'settings.payment-methods.manual-table' },
        { target: 'settings.payment-methods.empty', copy: 'empty' },
      ],
    },
    { id: 'add', kind: 'action', target: 'settings.payment-methods.add', optional: true },
  ],
  related: ['orders.detail-actions', 'settings.shipping', 'reports.payments'],
});

export const shippingTour = defineTour({
  id: 'settings.shipping',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/shipping',
  scope: 'store',
  requires: 'settings.read',
  icon: Truck,
  steps: [
    readOnlyStep,
    { id: 'intro', kind: 'intro' },
    {
      id: 'zones',
      kind: 'explain',
      variants: [
        { target: 'settings.shipping.zone' },
        { target: 'settings.shipping.empty', copy: 'empty' },
      ],
    },
    { id: 'rates', kind: 'explain', target: 'settings.shipping.rates', optional: true },
    {
      id: 'add-rate',
      kind: 'explain',
      target: 'settings.shipping.add-rate',
      requires: 'settings.write',
      optional: true,
    },
    {
      id: 'add-zone',
      kind: 'action',
      target: 'settings.shipping.add-zone',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['settings.tax', 'settings.locations'],
});

export const taxTour = defineTour({
  id: 'settings.tax',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/tax',
  scope: 'store',
  requires: 'settings.read',
  icon: Percent,
  steps: [
    readOnlyStep,
    { id: 'settings', kind: 'explain', target: 'settings.tax.settings' },
    { id: 'prices-include-tax', kind: 'explain', target: 'settings.tax.prices-include-tax' },
    {
      id: 'rates',
      kind: 'explain',
      variants: [{ target: 'settings.tax.empty', copy: 'empty' }, { target: 'settings.tax.rates' }],
      optional: true,
    },
    {
      id: 'add-rate',
      kind: 'explain',
      target: 'settings.tax.add-rate',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['settings.markets', 'settings.shipping'],
});

export const locationsTour = defineTour({
  id: 'settings.locations',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/locations',
  scope: 'store',
  requires: 'inventory.read',
  icon: Warehouse,
  steps: [
    readOnlyStep,
    {
      id: 'locations',
      kind: 'explain',
      variants: [
        { target: 'settings.locations.table' },
        { target: 'settings.locations.empty', copy: 'empty' },
      ],
    },
    {
      id: 'row-actions',
      kind: 'explain',
      target: 'settings.locations.row-actions',
      requires: 'inventory.write',
      optional: true,
    },
    {
      id: 'add',
      kind: 'explain',
      target: 'settings.locations.add',
      requires: 'inventory.write',
      optional: true,
    },
  ],
  related: ['inventory.levels', 'settings.shipping'],
});

export const returnsSettingsTour = defineTour({
  id: 'settings.returns',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/returns',
  scope: 'store',
  requires: 'settings.read',
  icon: Undo2,
  steps: [
    readOnlyStep,
    { id: 'window', kind: 'explain', target: 'settings.returns.window' },
    { id: 'fee', kind: 'explain', target: 'settings.returns.fee' },
    { id: 'paid-by', kind: 'explain', target: 'settings.returns.paid-by' },
    { id: 'default-returnable', kind: 'explain', target: 'settings.returns.default-returnable' },
    {
      id: 'save',
      kind: 'explain',
      target: 'settings.returns.save',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['returns.list', 'settings.policies'],
});

export const recoveryTour = defineTour({
  id: 'settings.recovery',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/recovery',
  scope: 'store',
  requires: 'settings.read',
  icon: ShoppingBag,
  steps: [
    readOnlyStep,
    { id: 'minutes', kind: 'explain', target: 'settings.recovery.minutes' },
    { id: 'link', kind: 'explain', target: 'settings.recovery.link' },
    {
      id: 'view-checkouts',
      kind: 'explain',
      target: 'settings.recovery.view-checkouts',
      requires: 'orders.read',
    },
  ],
  related: ['abandoned.list', 'settings.notifications'],
});

export const notificationsTour = defineTour({
  id: 'settings.notifications',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/notifications',
  scope: 'store',
  requires: { any: ['notifications.write', 'settings.read'] },
  icon: Mail,
  steps: [
    {
      id: 'sender',
      kind: 'explain',
      target: 'settings.notifications.sender',
      requires: 'stores.read',
      optional: true,
    },
    { id: 'family', kind: 'explain', target: 'settings.notifications.family' },
    {
      id: 'toggle',
      kind: 'explain',
      target: 'settings.notifications.toggle',
      requires: 'notifications.write',
    },
    { id: 'edit', kind: 'explain', target: 'settings.notifications.edit' },
  ],
  related: ['workflows.stage-settings', 'settings.recovery'],
});

export const policiesTour = defineTour({
  id: 'settings.policies',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/policies/',
  scope: 'store',
  requires: 'content.read',
  icon: ScrollText,
  steps: [
    readOnlyStep,
    { id: 'documents', kind: 'explain', target: 'settings.policies.documents' },
    {
      id: 'row-action',
      kind: 'explain',
      target: 'settings.policies.row-action',
      requires: 'content.write',
    },
    {
      id: 'facts',
      kind: 'explain',
      target: 'settings.policies.facts',
      requires: 'settings.read',
      optional: true,
    },
    {
      id: 'add-custom',
      kind: 'explain',
      target: 'settings.policies.add-custom',
      requires: 'content.write',
      optional: true,
    },
  ],
  related: ['settings.policy-editor', 'settings.returns'],
});

/** The policy someone edited last: the editor tour runs on it. */
const newestPolicy: TourResolver = {
  key: 'newest-policy',
  thing: 'things.policy',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const { items } = await getAdminV1StoresStoreIdPolicies({ storeId });
    const policy = items.reduce<(typeof items)[number] | undefined>(
      (newest, item) => (!newest || item.updatedAt > newest.updatedAt ? item : newest),
      undefined,
    );
    return policy ? { storeId, policyId: policy.id } : null;
  },
};

export const policyEditorTour = defineTour({
  id: 'settings.policy-editor',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/policies/$policyId',
  scope: 'record',
  resolve: newestPolicy,
  requires: 'content.read',
  icon: ScrollText,
  steps: [
    {
      id: 'details',
      kind: 'explain',
      target: 'settings.policy-editor.details',
      placement: 'right',
    },
    { id: 'body', kind: 'explain', target: 'settings.policy-editor.body', placement: 'right' },
    { id: 'status', kind: 'explain', target: 'settings.policy-editor.status', placement: 'left' },
    {
      id: 'save',
      kind: 'explain',
      target: 'settings.policy-editor.save',
      placement: 'left',
      requires: 'content.write',
    },
  ],
  related: ['settings.policies'],
});

export const feedsTour = defineTour({
  id: 'settings.feeds',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/feeds',
  scope: 'store',
  requires: 'content.read',
  icon: Rss,
  steps: [
    readOnlyStep,
    { id: 'channel', kind: 'explain', target: 'settings.feeds.channel' },
    { id: 'copy-url', kind: 'explain', target: 'settings.feeds.copy-url', optional: true },
    {
      id: 'rotate',
      kind: 'explain',
      target: 'settings.feeds.rotate',
      requires: 'content.write',
      optional: true,
    },
    { id: 'settings', kind: 'explain', target: 'settings.feeds.settings' },
  ],
  related: ['content.seo', 'catalog.products'],
});

export const analyticsDestinationsTour = defineTour({
  id: 'settings.analytics',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/analytics',
  scope: 'store',
  requires: 'settings.read',
  icon: ChartNoAxesCombined,
  steps: [
    readOnlyStep,
    { id: 'intro', kind: 'explain', target: 'settings.analytics.intro' },
    {
      id: 'destinations',
      kind: 'explain',
      variants: [
        { target: 'settings.analytics.destination' },
        { target: 'settings.analytics.empty', copy: 'empty' },
      ],
    },
    {
      id: 'connect',
      kind: 'explain',
      target: 'settings.analytics.connect',
      requires: 'settings.write',
      optional: true,
    },
  ],
  related: ['reports.analytics'],
});

export const importsExportsTour = defineTour({
  id: 'settings.imports-exports',
  version: 1,
  group: 'store-settings',
  route: '/_authed/_active/s/$storeId/settings/imports-exports',
  scope: 'store',
  requires: { any: ['exports.run', 'imports.run'] },
  icon: ArrowLeftRight,
  steps: [
    { id: 'tabs', kind: 'explain', target: 'settings.imports-exports.tabs' },
    {
      id: 'export-form',
      kind: 'explain',
      target: 'settings.imports-exports.export-form',
      requires: 'exports.run',
      optional: true,
      placement: 'right',
    },
    {
      id: 'export-jobs',
      kind: 'explain',
      target: 'settings.imports-exports.export-jobs',
      requires: 'exports.run',
      optional: true,
    },
    {
      id: 'imports-tab',
      kind: 'action',
      target: 'settings.imports-exports.imports-tab',
      requires: ['exports.run', 'imports.run'],
    },
    {
      id: 'import-form',
      kind: 'explain',
      target: 'settings.imports-exports.import-form',
      requires: 'imports.run',
      placement: 'right',
    },
  ],
  related: ['catalog.products'],
});
